import { useEffect, useRef, useState } from "react";
import { IRackModalProps } from "./RackModal.props";
import { useFirstRender } from "@utils/hooks/useFirstRender";
import { checkMinerInDeletedSections } from "./helpers";
import { useRackTemplateTypes } from "@utils/hooks/useRackTemplateTypes";

export type ActionType = "SELECT" | "OWN";

interface IRackModalContol {
  readonly templateTypesStrings: string[];
  readonly rackAction: ActionType;
  readonly rackType: [number, number];
  readonly widthValue: number;
  readonly heightValue: number;
  readonly lastSelectedRackType: [number, number];
  readonly fieldsError: boolean;
  readonly reversed: boolean;

  onChangeRackAction(action: ActionType): void;
  onChangeRackType(type: string): void;
  setWidthValue(v: number): void;
  setHeightValue(v: number): void;
  onChangeReversed(): void;
}

const INITIAL_TYPE: [number, number] = [10, 2];

export const useRackModalColntrol = (
  props: IRackModalProps
): IRackModalContol => {
  const firstRender = useFirstRender();
  const lastSelectedRackType = useRef<[number, number]>(
    props.rack?.rack_type || props.availableRack?.rack_type || INITIAL_TYPE
  );
  const { templateTypes, templateTypesStrings } = useRackTemplateTypes();
  const [rackAction, setRackAction] = useState<ActionType>(
    !!props.rack || props.availableRack ? "OWN" : "SELECT"
  );
  const [rackType, setRackType] = useState<[number, number]>(
    props.rack?.rack_type ||
      props.availableRack?.rack_type ||
      templateTypes[0] ||
      INITIAL_TYPE
  );
  const [widthValue, setWidthValue] = useState(
    props.rack?.rack_type[1] ||
      props.availableRack?.rack_type[1] ||
      INITIAL_TYPE[1]
  );
  const [heightValue, setHeightValue] = useState(
    props.rack?.rack_type[0] ||
      props.availableRack?.rack_type[0] ||
      INITIAL_TYPE[0]
  );
  const [fieldsError, setFieldsError] = useState(false);
  const [reversed, setReversed] = useState(false);

  const onChangeRackAction = (action: ActionType) => {
    switch (action) {
      case "SELECT":
        setRackType(lastSelectedRackType.current);
        break;
      case "OWN":
        setRackType([heightValue, widthValue]);
        break;
    }
    setRackAction(action);
  };

  const onChangeRackType = (type: string) => {
    const [w, h] = type.split("x").map((item) => +item) as [number, number];
    setRackType([h, w]);
    lastSelectedRackType.current = [h, w];
  };

  useEffect(() => {
    if (firstRender) return;
    setFieldsError(false);
    if (
      props.rack &&
      widthValue * heightValue !==
        props.rack.rack_type[0] * props.rack.rack_type[1] &&
      checkMinerInDeletedSections(props.rack.rack_items, props.rack.rack_type, [
        heightValue,
        widthValue,
      ])
    ) {
      setFieldsError(true);
      return;
    }

    const t = setTimeout(() => setRackType([heightValue, widthValue]), 200);
    return () => clearTimeout(t);
  }, [widthValue, heightValue]);

  useEffect(() => {
    if (firstRender) return;
    if (rackAction === "SELECT") {
      const t = templateTypes[0] || INITIAL_TYPE;
      setRackType(t);
    }
  }, [rackAction]);

  const onChangeReversed = () => setReversed((prev) => !prev);

  return {
    templateTypesStrings: !templateTypesStrings.length
      ? ["2x10"]
      : templateTypesStrings,
    rackAction,
    rackType,
    widthValue,
    heightValue,
    lastSelectedRackType: lastSelectedRackType.current,
    fieldsError,
    reversed,

    onChangeRackAction,
    onChangeRackType,
    setWidthValue,
    setHeightValue,
    onChangeReversed,
  };
};
